import React, { useMemo } from 'react';
import { Droplets, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import { haptics } from '../utils/haptics';

const CUP_ML = 250;

export default function WaterTracker({ currentMl = 0, targetMl = 2000, onChange }) {
  const { t } = useTranslation();
  const cups = Math.max(1, Math.round(targetMl / CUP_ML));
  const filledCups = Math.min(cups, Math.round(currentMl / CUP_ML));

  const droplets = useMemo(() => Array.from({ length: cups }, (_, i) => i < filledCups), [cups, filledCups]);

  const set = (next) => {
    const clamped = Math.max(0, Math.min(targetMl * 2, Math.round(next)));
    haptics.light?.();
    onChange?.(clamped);
  };

  return (
    <div className="rounded-lg border border-border/50 bg-card overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
        <div className="flex items-center gap-1.5">
          <Droplets className="h-4 w-4 text-blue-500" />
          <span className="text-sm font-medium">{t('water.title')}</span>
        </div>
        <span className="text-xs text-muted-foreground font-mono">
          {t('water.ofTarget', { current: Math.round(currentMl), target: Math.round(targetMl) })}
        </span>
      </div>
      <div className="px-3 py-3 space-y-3">
        {/* Water-cup buttons are 44px tall on mobile (touch-target rule) and
            compact 32px on desktop. Was 36×28 — below iOS minimum, real users
            mis-tapped neighbours. */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {droplets.map((filled, i) => (
            <button
              key={i}
              type="button"
              onClick={() => set((i + 1) * CUP_ML)}
              aria-label={`${(i + 1) * CUP_ML} ml`}
              className={`min-h-[44px] min-w-[44px] md:min-h-[32px] md:min-w-[32px] rounded-md border transition-colors flex items-center justify-center active:scale-95
                ${filled ? 'bg-blue-500/20 border-blue-500/60 text-blue-500' : 'bg-muted/40 border-border/60 text-muted-foreground/40'}`}
            >
              <Droplets className="h-4 w-4" />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => set(currentMl + 250)}>{t('water.addCup')}</Button>
          <Button size="sm" variant="outline" onClick={() => set(currentMl + 500)}>{t('water.addBottle')}</Button>
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => set(0)} aria-label={t('water.reset')}>
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
